import { useEffect, useState } from "react";
import Calendar from 'react-calendar';
import { IFReservationCalendar } from "views/utils/CommonConst";
import { dateToString8, getTimeRange, string8ToDate } from "views/utils/CommonFunc";
import { IFUseOrder } from "views/utils/UseOrder";

interface IFProps {
    isShow: boolean,
    setIsShow: React.Dispatch<React.SetStateAction<boolean>>,
    rCalendar: IFReservationCalendar[],
    index: number,
    order: IFUseOrder,
}

interface IFTimes {
    time: string,
    isVacant: boolean,
}

interface IFSelectDateTime {
    date: string,
    schedule_start_time: string,
    schedule_end_time: string,
}

export function ReservationCalendar(props: IFProps): JSX.Element{

    const [value, onChange] = useState<Date | null>(null);
    const [selectDateTime, setSelectDateTime] 
        = useState<IFSelectDateTime>({date: "", schedule_start_time: "", schedule_end_time: ""});   //選択された日付・時間の組み合わせを保持
    const [times, setTimes] = useState<IFTimes[]>([]);
    const [selectDate, setSelectDate] = useState<IFReservationCalendar>();  //日付情報全体を保持
    const [selectTimeIdx, setSelectTimeIdx] = useState<number>(-1);         //選択された時間のインデックスを保持
    let showClass = "";

    //表示時処理
    if (props.isShow){
        showClass = "is-show";
    }
    useEffect(function(){
        switch(props.index){
            case 0:
                if (props.order.data.reserve_date_1){
                    setInitDateTime(props.order.data.reserve_date_1, props.order.data.reserve_start_time_1, props.order.data.reserve_end_time_1);
                } else {
                    onChange(null);
                    setTimes([]);
                    setSelectTimeIdx(-1);
                }
                break;
            case 1:
                if (props.order.data.reserve_date_2){
                    setInitDateTime(props.order.data.reserve_date_2, props.order.data.reserve_start_time_2, props.order.data.reserve_end_time_2);
                } else {
                    onChange(null);
                    setTimes([]);
                    setSelectTimeIdx(-1);
                }
                break;
            case 2:
                if (props.order.data.reserve_date_3){
                    setInitDateTime(props.order.data.reserve_date_3, props.order.data.reserve_start_time_3, props.order.data.reserve_end_time_3);
                } else {
                    onChange(null);
                    setTimes([]);
                    setSelectTimeIdx(-1);
                }
                break;
        }
    }, [props.isShow]);

    //初期選択処理
    function setInitDateTime(strdate: string, start_time:string, end_time:string){
        let dDate:Date = string8ToDate(strdate);
        onChange(dDate);
        clickDate(dDate);
        const selectRCalendarItem = props.rCalendar.find(function(item){return item.date === strdate});
        if (selectRCalendarItem){
            selectRCalendarItem.calendar_list.forEach(function(time, idx){
                if (time.schedule_start_time === start_time && time.schedule_end_time === end_time){
                    setSelectTimeIdx(idx);
                }
            });      
        }
    }

    //値を設定する
    function clickSubmit(){
        let tmpOrder = {...props.order.data};
        switch(props.index){
            case 0:
                tmpOrder.reserve_date_1 = selectDateTime.date;
                tmpOrder.reserve_start_time_1 = selectDateTime.schedule_start_time;
                tmpOrder.reserve_end_time_1 = selectDateTime.schedule_end_time;
                break;
            case 1:
                tmpOrder.reserve_date_2 = selectDateTime.date;
                tmpOrder.reserve_start_time_2 = selectDateTime.schedule_start_time;
                tmpOrder.reserve_end_time_2 = selectDateTime.schedule_end_time;
                break;
            case 2:
                tmpOrder.reserve_date_3 = selectDateTime.date;
                tmpOrder.reserve_start_time_3 = selectDateTime.schedule_start_time;
                tmpOrder.reserve_end_time_3 = selectDateTime.schedule_end_time;
                break;
        }
        props.order.setData(tmpOrder);
        props.setIsShow(false);
    }

    //モーダルを閉じる
    function clickClose(){
        props.setIsShow(false);
    }

    //日付をクリック
    function clickDate(date:Date){
        //該当のデータを取得
        const selectRCalendarItem = props.rCalendar.find(function(item){return item.date === dateToString8(date)});
        if (selectRCalendarItem){
            //日付を保持
            setSelectDate(selectRCalendarItem);
            //時間を表示
            let tmpTimes:IFTimes[] = [];
            selectRCalendarItem.calendar_list.forEach(function(time){
                tmpTimes.push({time: getTimeRange(time.schedule_start_time, time.schedule_end_time), isVacant: time.status == "1" ? false : true});
            });
            setTimes(tmpTimes);
        }
        setSelectTimeIdx(-1);
    }

    //時間をクリック
    function clickTime(idx:number){
        setSelectTimeIdx(idx);
        //日付と時間の組み合わせを保持
        if (selectDate){
            let tmpSelectDateTime:IFSelectDateTime = {
                date: selectDate.date,
                schedule_start_time: selectDate.calendar_list[idx].schedule_start_time,
                schedule_end_time: selectDate.calendar_list[idx].schedule_end_time,
            }
            setSelectDateTime(tmpSelectDateTime);
        }
    }
    function getSelectedClass(idx:number):string{
        if (idx === selectTimeIdx){
            return " is-selected";
        }
        return "";
    }

    //開始日を取得
    function getFirstDate(){
        if (props.rCalendar.length > 0){
            return (string8ToDate(props.rCalendar[0].date));
        } else {
            return new Date();
        }
    }

    //終了日を取得
    function getLastDate(){
        if (props.rCalendar.length > 0){
            return string8ToDate(props.rCalendar[props.rCalendar.length - 1].date);
        } else {
            return new Date();
        }
    }

    //非活性状態を取得
    function getDisableStatus(date:Date):boolean{
        if (props.rCalendar){
            let rCalendarItem = props.rCalendar.find(function(item){return item.date === dateToString8(date)});
            if(!rCalendarItem){
                //日付がない場合、非営業日
                return true;
            } else {
                if (!isVacant(rCalendarItem)){
                    //全枠が埋まっている場合、空き無し
                    return true;
                }
            }
        }
        return false;
    }

    //ステータスによるクラスを取得
    function getStatusClass(date:Date):string{
        if (props.rCalendar){
            let rCalendarItem = props.rCalendar.find(function(item){return item.date === dateToString8(date)});
            if(!rCalendarItem){
                //日付がない場合、非営業日
                return "is-nonbusiness";
            } else {
                //一つでも空き時間があれば空きあり
                if (isVacant(rCalendarItem)){
                    return "is-vacant";
                } else {
                    //全枠が埋まっている場合、空き無し
                    return "is-full";
                }
            }
        }
        return "";
    }

    //空き判定
    function isVacant(rCalendarItem:IFReservationCalendar){
        let vacantFlg = false;
        rCalendarItem.calendar_list.some(function(time){
            if (time.status == "0"){
                vacantFlg = true;
                return;
            }
        });
        return vacantFlg;
    }

    //設定ボタンの活性制御
    function confirmIsDisabled(){
        if (selectTimeIdx === -1){
            return true;
        } else {
            return false;
        }
    }

    return (
        <div className={"modal " + showClass} id="modal-select-datetime">
            <div className="modal-back" onClick={clickClose}></div>
            <div className="modal-box">
                <div className="order-calendar">
                    <div className="order-calendar-date">
                        <div className="order-calendar-desc">①日付を選択してください</div>
                        <Calendar 
                            locale="ja-JP" 
                            formatDay={function(locale, date){return date.getDate().toString()}} 
                            onChange={onChange} 
                            value={value} 
                            onClickDay={function(date){clickDate(date)}}
                            prevLabel={<span className="material-icons-round">navigate_before</span>} 
                            nextLabel={<span className="material-icons-round">navigate_next</span>} 
                            prev2Label={null} next2Label={null} 
                            className={"calendar-guest-reservation"}
                            minDate={getFirstDate()} 
                            maxDate={getLastDate()}
                            minDetail={"month"}
                            maxDetail={"month"}
                            view={"month"}
                            tileClassName={function({activeStartDate, date, view }){return getStatusClass(date)}}
                            tileDisabled={function({activeStartDate, date, view }){return getDisableStatus(date)}}
                        />
                    </div>
                    <div className="order-calendar-time">
                        <div className="order-calendar-desc">②時間を選択してください</div>
                        <div className="calendar-time">
                            {times.length === 0 && <p className="calendar-time-msg">先に希望日を選択してください</p>}
                            <ul className="calender-time-list">
                                {times.map(function(time, idx){
                                    return (
                                        <li key={idx}>
                                            {time.isVacant ? 
                                                <button className={"btn-calendar-time" + getSelectedClass(idx)} data-time={time.time} onClick={function(){clickTime(idx)}}>
                                                    <span className="icon icon-status-circle"></span>{time.time}
                                                </button> :
                                                <button className="btn-calendar-time" data-time={time.time} disabled={true}>
                                                    <span className="icon icon-status-cross"></span>{time.time}
                                                </button>
                                            }
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="control-horizon control-center margin-t-20 btn-reservation-calendar">
                    <button className="btn-primary btn-setdatetime" onClick={clickSubmit} disabled={confirmIsDisabled()}>この希望日時を設定する</button>
                    <button className="btn-secondary" onClick={clickClose}>キャンセル</button>
                </div>
            </div>
        </div>
    )

}